"use client";

// src/components/organisms/landing/landing-link-analytics.tsx
// One delegated `click` listener for the landing surface's `cta_click`
// and `outbound_click` events, so `hero.tsx`, `site-header.tsx` and
// `cta-button.tsx` can stay server components — this component only reads
// the `data-cta-placement` markers they render. Mounted once in
// `landing-shell.tsx`, which wraps the landing AND the `[...slug]` WordPress
// pages (the acquisition surface). Navigation is never touched:
// `preventDefault` is never called.

import { useEffect } from "react";
import { capture } from "@/lib/posthog/client";
import { EVENTS } from "@/lib/posthog/events";
import { ctaDestination, ctaLabel, isCtaPlacement, linkText, outboundHost } from "@/lib/posthog/link-events";
import { readTranslated } from "@/lib/posthog/translation";

export function LandingLinkAnalytics() {
  useEffect(() => {
    function onClick(event: MouseEvent) {
      const target = event.target as Element | null;
      const anchor = target?.closest?.("a[href]") ?? null;
      if (!anchor) return;

      const href = anchor.getAttribute("href") ?? "";
      const text = linkText(anchor.textContent);
      const translated = readTranslated();

      const placementEl = anchor.closest("[data-cta-placement]");
      const placement = placementEl?.getAttribute("data-cta-placement");
      if (isCtaPlacement(placement)) {
        const label = ctaLabel(placementEl?.getAttribute("data-cta-label"));
        capture(EVENTS.CTA_CLICK, {
          placement,
          ...(label === undefined ? {} : { cta_label: label }),
          cta_destination: ctaDestination(href),
          translated,
        });
      }

      const host = outboundHost(href, window.location.host);
      if (host !== null) {
        capture(EVENTS.OUTBOUND_CLICK, { href: host, link_text: text, translated });
      }
    }

    // `click` only. A middle-click fires `auxclick`, not `click`, so opening
    // a CTA in a background tab with the middle button is a known one-sided
    // under-count — the weaker signal for a "clicked" metric — and listening
    // for both would need a `button === 1` filter to avoid counting the same
    // intent twice. Documented rather than hidden.
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return null;
}
